// Per-cylinder diagnostic modal — opened from a "Diagnose" button on
// any clip card with cylinder-health data. Shows three views:
//   1. Per-cylinder cards (primary, mechanic-style traffic-light grid)
//   2. Cycle-by-cycle timeline heatmap (intermittent vs constant faults)
//   3. Polar 720° engine-balance wheel
//
// All metrics come from `result.components.cylinders`, which is computed
// by Octave's compute_cylinder_health helper. No client-side DSP.
//
// This file is duplicated in admin/src/DiagnosticModal.tsx — keep in sync.

import { ReactNode } from 'react';
import { useState } from 'react';
import {
  AnalysisResult, CylinderHealthResult, PerCylinderMetrics,
  CycleFingerprintAlignment, PowertrainStateResult,
  AccessoryResult, VacuumLeakResult,
  BearingResult, AcCyclingResult, EventTimelineResult, DetectedEvent,
} from './lib/ws';
import { RepairLogModal } from './RepairLogModal';

interface Props {
  result: AnalysisResult;
  vehicleId?: number;
  vehicleName?: string;
  recordingId?: number;
  /** When provided, the modal renders a "Drift vs baseline" section
   *  comparing key metrics. */
  baseline?: AnalysisResult | null;
  /** JSON map from template-frame slot index → physical cyl number,
   *  set per-vehicle via the cylinder calibration modal (Step E).
   *  When present, per-cylinder cards display the user-assigned cyl
   *  number prominently and demote the persistent ID to a sub-label. */
  cylAssignmentsJson?: string | null;
  onClose: () => void;
}

export function DiagnosticModal({
  result, vehicleId, vehicleName, recordingId, baseline, cylAssignmentsJson, onClose,
}: Props) {
  const cyl = result.components.cylinders;
  const rpm = result.components.rpm;
  const [showRepairModal, setShowRepairModal] = useState(false);
  // Decode the per-vehicle template_slot → physical_cyl map (Step E)
  // once, and pass to the per-cyl cards so they can show the assigned
  // cyl number when present.
  const assignments: Record<number, number> = (() => {
    if (!cylAssignmentsJson) return {};
    try {
      const raw = JSON.parse(cylAssignmentsJson) as Record<string, number>;
      const out: Record<number, number> = {};
      for (const [k, v] of Object.entries(raw)) {
        const slot = Number(k);
        if (Number.isInteger(slot) && Number.isInteger(v)) out[slot] = v;
      }
      return out;
    } catch {
      return {};
    }
  })();

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 980, width: '95%', maxHeight: '92vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>
            Diagnostic
            {recordingId != null && (
              <span className="muted" style={{ fontWeight: 400, fontSize: '0.85rem' }}>
                {' '}— recording #{recordingId}
              </span>
            )}
          </h3>
          <div className="row" style={{ alignItems: 'center', gap: 6 }}>
            {vehicleId != null && (cyl?.num_cylinders ?? 0) > 0 && (
              <button
                className="secondary"
                onClick={() => setShowRepairModal(true)}
                title="Record a maintenance event so trends can mark it"
              >
                Log repair
              </button>
            )}
            <button className="modal-close" onClick={onClose} aria-label="close">×</button>
          </div>
        </div>
        {showRepairModal && vehicleId != null && (
          <RepairLogModal
            vehicleId={vehicleId}
            vehicleName={vehicleName}
            numCylinders={cyl?.num_cylinders ?? 4}
            onClose={() => setShowRepairModal(false)}
            onSaved={() => { /* trends modal will refetch on next open */ }}
          />
        )}

        <DiagnosticHeader
          vehicleName={vehicleName}
          rpm={rpm}
          cylinders={cyl}
          powertrain={result.components.powertrain_state}
        />

        {!cyl || !cyl.cylinders || cyl.cylinders.length === 0 ? (
          <div className="muted" style={{ padding: '1rem 0' }}>
            Cylinder-health data not available.
            {cyl?.error && <> ({cyl.error})</>}
          </div>
        ) : (
          <>
            <Section title="Per-cylinder summary">
              <CylinderCards data={cyl} assignments={assignments} />
            </Section>
            <Section title="Cycle-by-cycle timeline">
              <CycleTimeline data={cyl} />
              <div className="muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
                Each row a cylinder, each column a recorded engine cycle. Cell
                brightness = energy in that slot. A consistent dark stripe = dead
                cylinder; intermittent dark cells = misfire.
              </div>
            </Section>
            <Section title="Engine balance (polar 720° view)">
              <PolarBalance data={cyl} />
              <div className="muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
                Each wedge represents one cylinder's firing position in the
                720° engine cycle. Wedge tone = health flag; bar length =
                relative energy. The mechanic's "balance gauge" view.
              </div>
            </Section>
          </>
        )}

        {baseline && (
          <Section title="Drift vs baseline">
            <DriftPanel current={result} baseline={baseline} />
          </Section>
        )}

        {result.components.accessories && result.components.accessories.length > 0 && (
          <Section title="Accessory drive">
            <AccessoryPanel accessories={result.components.accessories} />
          </Section>
        )}
        {result.components.vacuum_leak && result.components.vacuum_leak.score != null && (
          <Section title="Vacuum / induction leak">
            <VacuumLeakPanel leak={result.components.vacuum_leak} />
          </Section>
        )}
        {result.components.bearings && (result.components.bearings.peaks?.length ?? 0) > 0 && (
          <Section title="Bearing-fault candidates (envelope spectrum, 1–5 kHz band)">
            <BearingPanel data={result.components.bearings} />
          </Section>
        )}
        {result.components.ac_cycling && result.components.ac_cycling.health && (
          <Section title="AC compressor clutch">
            <AcCyclingPanel data={result.components.ac_cycling} />
          </Section>
        )}
        {result.components.events && (result.components.events.events?.length ?? 0) > 0 && (
          <Section title="Transient events">
            <EventTimelinePanel
              data={result.components.events}
              clipDurationSec={result.rms.sample_count / result.rms.sample_rate}
            />
          </Section>
        )}
      </div>
    </div>
  );
}

// ---- Accessory drive panel --------------------------------------------

const ACCESSORY_LABELS: Record<string, string> = {
  alternator:     'Alternator',
  ac_compressor:  'AC compressor',
  power_steering: 'Power steering',
  water_pump:     'Water pump',
};

function AccessoryPanel({ accessories }: { accessories: AccessoryResult[] }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '0.5rem',
    }}>
      {accessories.map((a) => {
        const tone = a.health === 'green' ? 'var(--ok)'
                   : a.health === 'yellow' ? '#f59e0b'
                   : a.health === 'absent' ? 'var(--muted)'
                   : 'var(--danger)';
        const headlineLabel = a.health === 'green' ? 'OK'
                            : a.health === 'yellow' ? 'WEAK / OFF-FREQ'
                            : a.health === 'absent' ? 'NOT DETECTED'
                            : 'UNKNOWN';
        const devPct = a.peak_hz != null && a.expected_hz > 0
          ? ((a.peak_hz - a.expected_hz) / a.expected_hz) * 100
          : null;
        return (
          <div key={a.name} style={{
            background: 'var(--panel-2)',
            border: `1px solid ${tone}`,
            borderTop: `3px solid ${tone}`,
            borderRadius: 8,
            padding: '0.5rem 0.7rem',
          }}>
            <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <strong>{ACCESSORY_LABELS[a.name] ?? a.name}</strong>
              <span style={{ color: tone, fontSize: '0.7rem', fontWeight: 700 }}>
                {headlineLabel}
              </span>
            </div>
            <div style={{ marginTop: 4, fontSize: '0.85rem' }}>
              Expected: <strong>{a.expected_hz.toFixed(1)} Hz</strong>
              <span className="muted" style={{ fontSize: '0.75rem' }}>
                {' '}(× {a.pulley_ratio} pulley)
              </span>
            </div>
            {a.peak_hz != null && (
              <div style={{ fontSize: '0.85rem' }}>
                Detected: <strong>{a.peak_hz.toFixed(1)} Hz</strong>
                {devPct != null && Math.abs(devPct) > 0.5 && (
                  <span className="muted" style={{ fontSize: '0.75rem' }}>
                    {' '}({devPct >= 0 ? '+' : ''}{devPct.toFixed(1)}%)
                  </span>
                )}
              </div>
            )}
            {a.snr != null && (
              <div className="muted" style={{ fontSize: '0.78rem' }}>
                SNR: {a.snr.toFixed(1)}
                {a.snr < 3 && <span style={{ color: 'var(--muted)' }}> (below detection)</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---- Drift vs baseline panel ------------------------------------------
//
// Compares key metrics between this clip and the vehicle's baseline.
// The mechanic value is the DELTA — an alternator that crept from
// SNR 50 to SNR 8 between clips is a failing alternator, even if SNR 8
// alone might still pass the absolute threshold.

function DriftPanel({
  current, baseline,
}: { current: AnalysisResult; baseline: AnalysisResult }) {
  const cur = current.components;
  const base = baseline.components;
  const rows: Array<{ label: string; before: string; after: string; delta: string; tone: string }> = [];

  if (cur.cylinders?.smoothness_score != null && base.cylinders?.smoothness_score != null) {
    const d = cur.cylinders.smoothness_score - base.cylinders.smoothness_score;
    rows.push({
      label: 'Smoothness score',
      before: base.cylinders.smoothness_score.toFixed(0),
      after: cur.cylinders.smoothness_score.toFixed(0),
      delta: `${d >= 0 ? '+' : ''}${d.toFixed(0)}`,
      tone: d < -10 ? 'var(--danger)' : d < -3 ? '#f59e0b' : 'var(--ok)',
    });
  }
  if (cur.cylinders?.exhaust_leak_score != null && base.cylinders?.exhaust_leak_score != null) {
    const d = cur.cylinders.exhaust_leak_score - base.cylinders.exhaust_leak_score;
    rows.push({
      label: 'Exhaust leak score',
      before: base.cylinders.exhaust_leak_score.toFixed(2),
      after: cur.cylinders.exhaust_leak_score.toFixed(2),
      delta: `${d >= 0 ? '+' : ''}${d.toFixed(2)}`,
      tone: d > 0.15 ? 'var(--danger)' : d > 0.05 ? '#f59e0b' : 'var(--ok)',
    });
  }
  if (cur.vacuum_leak?.score != null && base.vacuum_leak?.score != null) {
    const d = cur.vacuum_leak.score - base.vacuum_leak.score;
    rows.push({
      label: 'Vacuum leak score',
      before: base.vacuum_leak.score.toFixed(2),
      after: cur.vacuum_leak.score.toFixed(2),
      delta: `${d >= 0 ? '+' : ''}${d.toFixed(2)}`,
      tone: d > 0.15 ? 'var(--danger)' : d > 0.05 ? '#f59e0b' : 'var(--ok)',
    });
  }

  // Per-accessory SNR drift.
  if (cur.accessories && base.accessories) {
    for (const acc of cur.accessories) {
      const baseAcc = base.accessories.find((b) => b.name === acc.name);
      if (baseAcc?.snr != null && acc.snr != null) {
        const d = acc.snr - baseAcc.snr;
        const dPct = baseAcc.snr > 0 ? (d / baseAcc.snr) * 100 : 0;
        rows.push({
          label: `${ACCESSORY_LABELS[acc.name] ?? acc.name} SNR`,
          before: baseAcc.snr.toFixed(1),
          after: acc.snr.toFixed(1),
          delta: `${dPct >= 0 ? '+' : ''}${dPct.toFixed(0)}%`,
          tone: dPct < -50 ? 'var(--danger)' : dPct < -20 ? '#f59e0b' : 'var(--ok)',
        });
      }
    }
  }

  // Per-cylinder energy drift.
  if (cur.cylinders?.cylinders && base.cylinders?.cylinders
      && cur.cylinders.cylinders.length === base.cylinders.cylinders.length) {
    for (let i = 0; i < cur.cylinders.cylinders.length; i++) {
      const a = cur.cylinders.cylinders[i];
      const b = base.cylinders.cylinders[i];
      const d = a.energy_pct_of_cohort - b.energy_pct_of_cohort;
      if (Math.abs(d) >= 5) {
        rows.push({
          label: `cyl ${a.index} energy`,
          before: `${b.energy_pct_of_cohort.toFixed(0)}%`,
          after: `${a.energy_pct_of_cohort.toFixed(0)}%`,
          delta: `${d >= 0 ? '+' : ''}${d.toFixed(0)}%`,
          tone: Math.abs(d) > 20 ? 'var(--danger)' : Math.abs(d) > 10 ? '#f59e0b' : 'var(--ok)',
        });
      }
    }
  }

  if (rows.length === 0) {
    return (
      <div className="muted" style={{ fontSize: '0.85rem' }}>
        No comparable metrics between current clip and baseline.
      </div>
    );
  }

  return (
    <table style={{ fontSize: '0.85rem' }}>
      <thead>
        <tr style={{ color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
          <th style={{ textAlign: 'left' }}>metric</th>
          <th style={{ textAlign: 'right' }}>baseline</th>
          <th style={{ textAlign: 'right' }}>current</th>
          <th style={{ textAlign: 'right' }}>Δ</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td>{r.label}</td>
            <td style={{ textAlign: 'right' }}>{r.before}</td>
            <td style={{ textAlign: 'right' }}>{r.after}</td>
            <td style={{ textAlign: 'right', color: r.tone, fontWeight: 700 }}>{r.delta}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function VacuumLeakPanel({ leak }: { leak: VacuumLeakResult }) {
  const tone = leak.health === 'green' ? 'var(--ok)'
             : leak.health === 'yellow' ? '#f59e0b'
             : 'var(--danger)';
  const score = leak.score ?? 0;
  return (
    <div style={{
      background: 'var(--panel-2)',
      border: `1px solid ${tone}`,
      borderRadius: 8,
      padding: '0.6rem 0.8rem',
    }}>
      <div className="row" style={{ alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <div className="muted" style={{ fontSize: '0.78rem' }}>Leak score (0–1)</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 700, color: tone }}>
            {score.toFixed(2)}
          </div>
        </div>
        <div className="muted" style={{ fontSize: '0.85rem', flex: 1, maxWidth: 480 }}>
          Ratio of NON-cycle-locked energy to total energy in the
          {' '}{leak.band_lo_hz}–{leak.band_hi_hz} Hz band. High values
          suggest a vacuum / induction leak (continuous hiss). Engine
          firing harmonics are subtracted before scoring.
          {leak.health === 'green' && ' No leak detected.'}
          {leak.health === 'yellow' && ' Borderline — investigate if symptoms persist.'}
          {leak.health === 'red' && ' Leak strongly suspected.'}
        </div>
      </div>
      {/* Bar showing where the score sits on the scale. */}
      <div style={{
        marginTop: 6, height: 8, background: 'var(--panel)',
        borderRadius: 4, overflow: 'hidden', position: 'relative',
      }}>
        <div style={{
          width: `${Math.min(100, score * 100)}%`,
          height: '100%', background: tone,
        }} />
        <div style={{
          position: 'absolute', left: '40%', top: 0,
          width: 1, height: '100%', background: 'var(--border)',
        }} />
        <div style={{
          position: 'absolute', left: '65%', top: 0,
          width: 1, height: '100%', background: 'var(--border)',
        }} />
      </div>
      <div className="muted" style={{ fontSize: '0.7rem', marginTop: 2 }}>
        Threshold marks: 0.40 (yellow), 0.65 (red)
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: '1rem' }}>
      <h4 style={{ margin: '0 0 0.5rem 0' }}>{title}</h4>
      {children}
    </div>
  );
}

function DiagnosticHeader({
  vehicleName, rpm, cylinders, powertrain,
}: {
  vehicleName?: string;
  rpm?: number;
  cylinders?: CylinderHealthResult;
  powertrain?: PowertrainStateResult;
}) {
  const global = cylinders?.global_health;
  const tone = global === 'red' ? 'var(--danger)'
             : global === 'yellow' ? '#f59e0b'
             : global === 'green' ? 'var(--ok)'
             : 'var(--muted)';
  return (
    <div style={{
      background: 'var(--panel-2)',
      border: `1px solid ${tone}`,
      borderRadius: 8,
      padding: '0.6rem 0.9rem',
      display: 'flex',
      alignItems: 'baseline',
      gap: '1.5rem',
      flexWrap: 'wrap',
    }}>
      <div>
        <div className="muted" style={{ fontSize: '0.78rem' }}>Engine</div>
        <div style={{ fontSize: '1.05rem' }}>{vehicleName ?? '—'}</div>
      </div>
      {rpm != null && (
        <div>
          <div className="muted" style={{ fontSize: '0.78rem' }}>RPM</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{Math.round(rpm)}</div>
        </div>
      )}
      {cylinders && (
        <div>
          <div className="muted" style={{ fontSize: '0.78rem' }}>Cycles analysed</div>
          <div>{cylinders.cycles_analyzed}</div>
        </div>
      )}
      {cylinders?.smoothness_score != null && (
        <div>
          <div className="muted" style={{ fontSize: '0.78rem' }}>Smoothness</div>
          <div style={{
            fontWeight: 700,
            color: cylinders.smoothness_score >= 90 ? 'var(--ok)'
                 : cylinders.smoothness_score >= 75 ? '#f59e0b'
                 : 'var(--danger)',
          }}>
            {cylinders.smoothness_score.toFixed(0)} / 100
          </div>
        </div>
      )}
      {cylinders?.exhaust_leak_score != null && (
        <div>
          <div className="muted" style={{ fontSize: '0.78rem' }}>Exhaust leak</div>
          <div style={{
            fontWeight: 700,
            color: cylinders.exhaust_leak_score < 0.5 ? 'var(--ok)'
                 : cylinders.exhaust_leak_score < 0.65 ? '#f59e0b'
                 : 'var(--danger)',
          }}>
            {cylinders.exhaust_leak_score.toFixed(2)}
          </div>
          <div className="muted" style={{ fontSize: '0.7rem' }}>
            {cylinders.exhaust_leak_score < 0.5 ? 'no leak'
              : cylinders.exhaust_leak_score < 0.65 ? 'borderline'
              : 'leak suspected'}
          </div>
        </div>
      )}
      {global && (
        <div>
          <div className="muted" style={{ fontSize: '0.78rem' }}>Overall health</div>
          <div style={{ color: tone, fontWeight: 700, textTransform: 'uppercase' }}>
            {global}
          </div>
        </div>
      )}
      {cylinders?.cycle_fingerprint_alignment && (
        <div>
          <div className="muted" style={{ fontSize: '0.78rem' }}>Cyl alignment</div>
          <AlignmentBadge align={cylinders.cycle_fingerprint_alignment} />
        </div>
      )}
      {powertrain && (
        <div>
          <div className="muted" style={{ fontSize: '0.78rem' }}>Powertrain</div>
          <PowertrainBadge powertrain={powertrain} />
        </div>
      )}
    </div>
  );
}

/** Server-side equivalent of the live monitor's mode badge.
 *  Visible on every recording; primarily useful for hybrid vehicles
 *  where the user wants to know which mode produced this clip. For
 *  ICE-only it's a confidence signal that the analysis pipeline saw
 *  a real engine; for EV-only it's a sanity check that nothing
 *  pretended to be combustion. */
function PowertrainBadge({ powertrain }: { powertrain: PowertrainStateResult }) {
  const tone = powertrain.state === 'engine' ? 'var(--accent)'
             : powertrain.state === 'ev' ? '#10b981'
             : 'var(--muted)';
  return (
    <div>
      <div style={{ fontWeight: 700, color: tone, textTransform: 'uppercase' }}>
        {powertrain.state}
      </div>
      <div className="muted" style={{ fontSize: '0.7rem' }}>
        harm. ratio {powertrain.harmonic_ratio.toFixed(2)}
        {' · '}{powertrain.intensity_dbfs.toFixed(0)} dBFS
      </div>
    </div>
  );
}

/** Compact rotation-confidence indicator. Shows in the header so the
 *  mechanic can tell at a glance whether the per-cyl cards below are
 *  comparable to past recordings (high confidence) or labelled by raw
 *  slot only (low confidence — expected on the first few clips per
 *  vehicle, before the template settles). */
function AlignmentBadge({ align }: { align: CycleFingerprintAlignment }) {
  const conf = align.confidence;
  const tone = align.bootstrap ? 'var(--muted)'
             : conf >= 0.7 ? 'var(--ok)'
             : conf >= 0.4 ? '#f59e0b'
             : 'var(--danger)';
  return (
    <div>
      <div style={{ fontWeight: 700, color: tone }}>
        {align.bootstrap
          ? 'TEMPLATE BOOTSTRAP'
          : `r=${align.rotation} · ${(conf * 100).toFixed(0)}%`}
      </div>
      <div className="muted" style={{ fontSize: '0.7rem' }}>
        template: {align.template_count} clip{align.template_count === 1 ? '' : 's'}
      </div>
    </div>
  );
}

// ---- Per-cylinder cards (primary view) ---------------------------------

function CylinderCards({
  data, assignments,
}: { data: CylinderHealthResult; assignments: Record<number, number> }) {
  const align = data.cycle_fingerprint_alignment;
  const N = data.num_cylinders;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))`,
      gap: '0.5rem',
    }}>
      {data.cylinders.map((c) => {
        // Persistent ID = template-frame slot index for this card.
        // Recording-frame slot s maps to template-frame slot
        // (s + rotation) mod N. Suppress the label when alignment is
        // either missing or weak — showing a "P-?" everywhere would
        // be more confusing than helpful.
        let persistentId: string | null = null;
        let assignedCyl: number | null = null;
        if (align && N > 0) {
          const t = ((c.slot_index + align.rotation) % N + N) % N;
          const letter = String.fromCharCode(65 + t);
          persistentId = align.confidence >= 0.7 ? `P-${letter}`
                      : align.confidence >= 0.4 ? `P-${letter}?`
                      : null;
          // If the user has calibrated this template slot to a
          // physical cyl, use that as the headline label and demote
          // the firing-order index. Only when alignment is reasonably
          // confident — under that threshold we don't trust the
          // mapping enough to override.
          if (align.confidence >= 0.5 && assignments[t] != null) {
            assignedCyl = assignments[t];
          }
        }
        return (
          <CylinderCard
            key={c.slot_index}
            c={c}
            persistentId={persistentId}
            assignedCyl={assignedCyl}
          />
        );
      })}
    </div>
  );
}

function CylinderCard({
  c, persistentId, assignedCyl,
}: {
  c: PerCylinderMetrics;
  persistentId: string | null;
  assignedCyl: number | null;
}) {
  const tone = c.health === 'red' ? 'var(--danger)'
             : c.health === 'yellow' ? '#f59e0b'
             : 'var(--ok)';
  const headlineLabel = c.health === 'red' ? 'CHECK'
                      : c.health === 'yellow' ? 'INVESTIGATE'
                      : 'OK';
  const dev = c.energy_pct_of_cohort - 100;
  const devTone = Math.abs(dev) > 25 ? 'var(--danger)'
               : Math.abs(dev) > 10 ? '#f59e0b'
               : 'var(--ok)';

  return (
    <div style={{
      background: 'var(--panel-2)',
      border: `1px solid ${tone}`,
      borderTop: `3px solid ${tone}`,
      borderRadius: 8,
      padding: '0.5rem 0.7rem',
    }}>
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <strong style={{ fontSize: '1rem' }}>
            cyl {assignedCyl ?? c.index}
          </strong>
          {(persistentId || (assignedCyl != null && assignedCyl !== c.index)) && (
            <span className="muted" style={{ fontSize: '0.7rem', marginLeft: 6 }}>
              {assignedCyl != null && assignedCyl !== c.index && (
                <>· firing-order #{c.index}</>
              )}
              {persistentId && (assignedCyl != null && assignedCyl !== c.index ? ' · ' : '· ')}
              {persistentId}
            </span>
          )}
        </div>
        <span style={{ color: tone, fontSize: '0.75rem', fontWeight: 700 }}>
          {headlineLabel}
        </span>
      </div>

      <div style={{ marginTop: 4, fontSize: '0.85rem' }}>
        <div>Energy: <strong>{c.energy_pct_of_cohort.toFixed(0)}%</strong>
          <span className="muted" style={{ fontSize: '0.78rem' }}> of cohort</span>
        </div>
        <div style={{ color: devTone, fontSize: '0.78rem' }}>
          Δ {dev >= 0 ? '+' : ''}{dev.toFixed(0)}%
        </div>
      </div>

      <Sparkline values={c.energy_per_cycle} thresholdRatio={0.3} />

      <div style={{ marginTop: 4, fontSize: '0.78rem' }} className="muted">
        Misfires: <span style={{ color: c.missed_count > 0 ? 'var(--danger)' : 'var(--fg)' }}>
          {c.missed_count}/{c.cycles_total}
        </span>
        {' · '}Var: {c.variance_pct.toFixed(0)}%
      </div>
      <div style={{ fontSize: '0.78rem' }} className="muted">
        Phase: {c.phase_offset_deg.toFixed(1)}°
        {' · '}HF: {(c.high_freq_ratio * 100).toFixed(0)}%
      </div>
      <SpectrumThumb thumb={c.spectrum_thumb} />

      {c.flags.length > 0 && (
        <ul style={{
          margin: '4px 0 0 0', padding: '0 0 0 18px',
          fontSize: '0.75rem', color: tone,
        }}>
          {c.flags.map((f, i) => (<li key={i}>{f}</li>))}
        </ul>
      )}

      <div className="muted" style={{ marginTop: 3, fontSize: '0.7rem' }}>
        confidence: {(c.confidence * 100).toFixed(0)}%
      </div>
      {c.vs_cohort_p != null && c.vs_cohort_d != null && (
        <CohortStatsLine p={c.vs_cohort_p} d={c.vs_cohort_d} />
      )}
    </div>
  );
}

/** Welch t-test of this cyl vs the pooled rest-of-cohort, showing
 *  effect size and p-value side-by-side. At typical cycle counts
 *  (>10) the t-test almost always returns significant, so the d
 *  effect size is the more honest summary — but reporting both keeps
 *  the readout statistically explicit. Color-codes: green/accent for
 *  meaningfully different (p<0.05 AND |d|>=0.2), faded otherwise. */
function CohortStatsLine({ p, d }: { p: number; d: number }) {
  const fmtP = (pv: number) =>
    pv < 1e-6 ? '<1e-6'
      : pv < 0.001 ? '<0.001'
      : pv.toFixed(3);
  const stars = p < 0.001 ? '***' : p < 0.01 ? '**' : p < 0.05 ? '*' : '';
  const meaningful = p < 0.05 && Math.abs(d) >= 0.2;
  return (
    <div className="muted" style={{
      fontSize: '0.7rem', marginTop: 1,
      color: meaningful
        ? (d > 0 ? 'var(--accent)' : '#10b981')
        : 'var(--muted)',
    }}
    title="Welch's t-test: this cyl's per-cycle RMS vs pooled rest-of-cyls. d = Cohen's d effect size; |d|<0.2 small, 0.5 medium, 0.8 large.">
      vs cohort: d={d.toFixed(2)} ({Math.abs(d) < 0.2 ? 'small' : Math.abs(d) < 0.5 ? 'small-med' : Math.abs(d) < 0.8 ? 'medium' : 'large'}),
      p={fmtP(p)}{stars}
    </div>
  );
}

function Sparkline({ values, thresholdRatio }: { values: number[]; thresholdRatio: number }) {
  if (values.length === 0) return null;
  const W = 200, H = 28;
  const max = Math.max(...values, 1e-12);
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const threshold = thresholdRatio * mean;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
         style={{ width: '100%', height: H, display: 'block', marginTop: 4 }}>
      {values.map((v, i) => {
        const x = (i / values.length) * W;
        const bw = W / values.length;
        const h = (v / max) * H;
        const fill = v < threshold ? 'var(--danger)' : 'var(--accent)';
        return (
          <rect
            key={i}
            x={x} y={H - h}
            width={Math.max(0.5, bw - 0.4)}
            height={h}
            fill={fill}
          />
        );
      })}
    </svg>
  );
}

function SpectrumThumb({ thumb }: { thumb: number[] }) {
  if (!thumb || thumb.length === 0) return null;
  const W = 200, H = 18;
  const max = Math.max(...thumb, 1e-12);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
         style={{ width: '100%', height: H, display: 'block', marginTop: 2 }}>
      {thumb.map((v, i) => {
        const x = (i / thumb.length) * W;
        const bw = W / thumb.length;
        const h = (v / max) * H;
        return (
          <rect
            key={i}
            x={x} y={H - h}
            width={Math.max(0.5, bw - 0.3)}
            height={h}
            fill="var(--accent-2)"
          />
        );
      })}
    </svg>
  );
}

// ---- Cycle-by-cycle timeline heatmap -----------------------------------

function CycleTimeline({ data }: { data: CylinderHealthResult }) {
  const cyls = data.cylinders;
  const numCyl = cyls.length;
  const numCycles = cyls[0]?.cycles_total ?? 0;
  if (numCycles === 0) return null;

  // Cohort mean used to colour cells.
  const cohort = data.cohort_mean_energy ?? 1;

  const rowH = 22;
  const colW = Math.min(20, Math.max(6, Math.floor(800 / numCycles)));
  const labelW = 50;
  const W = labelW + numCycles * colW;
  const H = numCyl * rowH;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMinYMid meet"
      style={{ width: '100%', maxHeight: H, display: 'block' }}
    >
      {cyls.map((cyl, r) => (
        <text
          key={`lbl-${r}`}
          x={4} y={r * rowH + rowH * 0.7}
          fill="var(--muted)"
          fontSize={11}
          fontFamily="ui-monospace, SFMono-Regular, monospace"
        >cyl {cyl.index}</text>
      ))}
      {cyls.map((cyl, r) =>
        cyl.energy_per_cycle.map((e, c) => {
          const ratio = Math.min(1, Math.max(0, e / (cohort * 1.5)));   // 0..1 vs 1.5× cohort
          const isMissed = e < 0.30 * cohort;
          const fill = isMissed
            ? 'var(--danger)'
            : `hsl(200, 60%, ${20 + ratio * 50}%)`;
          return (
            <rect
              key={`c-${r}-${c}`}
              x={labelW + c * colW}
              y={r * rowH + 2}
              width={colW - 1}
              height={rowH - 4}
              fill={fill}
            />
          );
        }))}
    </svg>
  );
}

// ---- Polar 720° engine-balance wheel -----------------------------------

function PolarBalance({ data }: { data: CylinderHealthResult }) {
  const cyls = data.cylinders;
  const N = cyls.length;
  if (N === 0) return null;
  const cohort = data.cohort_mean_energy ?? 1;
  const SIZE = 360;
  const CX = SIZE / 2, CY = SIZE / 2;
  const R_INNER = 60;
  const R_OUTER_BASE = 130;
  const R_OUTER_MAX = 165;

  const tone = (h: PerCylinderMetrics['health']): string =>
    h === 'red' ? 'var(--danger)'
    : h === 'yellow' ? '#f59e0b'
    : 'var(--ok)';

  // Each cylinder gets an angular slice; firing order proceeds clockwise
  // from 12 o'clock.
  const sliceAngle = (2 * Math.PI) / N;

  const polarToXY = (r: number, angle: number) => {
    // angle 0 = top (12 o'clock), positive clockwise.
    const a = angle - Math.PI / 2;
    return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
  };

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ width: '100%', maxWidth: SIZE, display: 'block', margin: '0 auto' }}>
      {/* Outer ring */}
      <circle cx={CX} cy={CY} r={R_OUTER_BASE} fill="none" stroke="var(--border)" strokeWidth="1" strokeDasharray="2 4" />
      {/* Inner hub */}
      <circle cx={CX} cy={CY} r={R_INNER} fill="var(--panel-2)" stroke="var(--border)" />
      <text x={CX} y={CY - 4} textAnchor="middle" fontSize="10" fill="var(--muted)">720°</text>
      <text x={CX} y={CY + 10} textAnchor="middle" fontSize="10" fill="var(--muted)">cycle</text>

      {cyls.map((c, i) => {
        const a0 = i * sliceAngle;
        const a1 = (i + 1) * sliceAngle;
        const aMid = (a0 + a1) / 2;
        const sliceTone = tone(c.health);
        // Slice background path (annular sector).
        const [x0a, y0a] = polarToXY(R_INNER, a0);
        const [x1a, y1a] = polarToXY(R_OUTER_BASE, a0);
        const [x0b, y0b] = polarToXY(R_INNER, a1);
        const [x1b, y1b] = polarToXY(R_OUTER_BASE, a1);
        const largeArc = sliceAngle > Math.PI ? 1 : 0;
        const slicePath = [
          `M ${x1a},${y1a}`,
          `A ${R_OUTER_BASE},${R_OUTER_BASE} 0 ${largeArc} 1 ${x1b},${y1b}`,
          `L ${x0b},${y0b}`,
          `A ${R_INNER},${R_INNER} 0 ${largeArc} 0 ${x0a},${y0a}`,
          'Z',
        ].join(' ');

        // Energy bar — extra radial extension proportional to energy_pct.
        const energyR = R_OUTER_BASE
          + Math.max(0, Math.min(R_OUTER_MAX - R_OUTER_BASE,
              ((c.energy_pct_of_cohort - 70) / 60) * (R_OUTER_MAX - R_OUTER_BASE)));
        const [bx0, by0] = polarToXY(R_OUTER_BASE, a0 + sliceAngle * 0.1);
        const [bx1, by1] = polarToXY(R_OUTER_BASE, a1 - sliceAngle * 0.1);
        const [bx2, by2] = polarToXY(energyR,      a1 - sliceAngle * 0.1);
        const [bx3, by3] = polarToXY(energyR,      a0 + sliceAngle * 0.1);
        const barPath = `M ${bx0},${by0} L ${bx1},${by1} L ${bx2},${by2} L ${bx3},${by3} Z`;

        const [lx, ly] = polarToXY((R_INNER + R_OUTER_BASE) / 2, aMid);

        return (
          <g key={i}>
            <path d={slicePath} fill={sliceTone} fillOpacity={0.18} stroke={sliceTone} strokeWidth="1" />
            <path d={barPath} fill={sliceTone} fillOpacity={0.85} />
            <text
              x={lx} y={ly + 4}
              textAnchor="middle"
              fontSize="13" fontWeight="700"
              fill="var(--fg)"
            >{c.index}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ---- Tier 3: bearing-fault peaks panel --------------------------------

function BearingPanel({ data }: { data: BearingResult }) {
  if (!data.peaks || data.peaks.length === 0) {
    return <div className="muted">No bearing-band peaks above noise floor.</div>;
  }
  const tone = (label: string): string => {
    if (label.includes('BPFI')) return 'var(--danger)';
    if (label.includes('BPFO')) return '#f59e0b';
    if (label.includes('BSF') || label.includes('FTF')) return '#fb7185';
    return 'var(--muted)';
  };
  return (
    <>
      <div className="muted" style={{ fontSize: '0.8rem', marginBottom: 4 }}>
        Shaft frequency: {data.shaft_freq_hz?.toFixed(2)} Hz
        ({data.shaft_rpm?.toFixed(0)} RPM). Engine harmonics already
        filtered. Each peak's ratio to shaft frequency is the standard
        bearing-fault classifier.
      </div>
      <table style={{ fontSize: '0.85rem',
                       fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
        <thead>
          <tr style={{ color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
            <th style={{ textAlign: 'right' }}>freq (Hz)</th>
            <th style={{ textAlign: 'right' }}>shaft ratio</th>
            <th style={{ textAlign: 'right' }}>SNR</th>
            <th style={{ textAlign: 'left', paddingLeft: 8 }}>candidate</th>
          </tr>
        </thead>
        <tbody>
          {data.peaks.map((p, i) => (
            <tr key={i}>
              <td style={{ textAlign: 'right' }}>{p.freq_hz.toFixed(1)}</td>
              <td style={{ textAlign: 'right' }}>{p.shaft_ratio.toFixed(2)}×</td>
              <td style={{ textAlign: 'right' }}>{p.snr.toFixed(1)}</td>
              <td style={{ paddingLeft: 8, color: tone(p.candidate) }}>{p.candidate}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
        FTF (cage) ~0.4×, BSF (ball spin) 2–4×, BPFO (outer race) 4–8×,
        BPFI (inner race) 5–12×. Ratios are diagnostic clues; mechanic
        should confirm by inspecting the indicated bearing.
      </div>
    </>
  );
}

// ---- Tier 3: AC compressor cycling panel ------------------------------

function AcCyclingPanel({ data }: { data: AcCyclingResult }) {
  const tone = data.health === 'short-cycling' ? 'var(--danger)'
             : data.health === 'cycling' || data.health === 'on' ? 'var(--ok)'
             : 'var(--muted)';
  return (
    <div style={{
      background: 'var(--panel-2)',
      border: `1px solid ${tone}`,
      borderRadius: 8,
      padding: '0.6rem 0.8rem',
    }}>
      <div className="row" style={{ alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <div className="muted" style={{ fontSize: '0.78rem' }}>State</div>
          <div style={{ fontWeight: 700, color: tone, textTransform: 'uppercase' }}>
            {data.health}
          </div>
        </div>
        {data.pct_running != null && (
          <div>
            <div className="muted" style={{ fontSize: '0.78rem' }}>Engaged</div>
            <div>{data.pct_running.toFixed(0)}% of clip</div>
          </div>
        )}
        {data.num_transitions != null && data.num_transitions > 0 && (
          <div>
            <div className="muted" style={{ fontSize: '0.78rem' }}>Transitions</div>
            <div>{data.num_transitions}</div>
          </div>
        )}
        {data.expected_hz != null && (
          <div>
            <div className="muted" style={{ fontSize: '0.78rem' }}>Compressor freq</div>
            <div>{data.expected_hz.toFixed(1)} Hz</div>
          </div>
        )}
      </div>
      {data.pattern && (
        <div className="muted" style={{ marginTop: 6, fontSize: '0.85rem' }}>
          {data.pattern}
        </div>
      )}
      {data.health === 'short-cycling' && (
        <div className="error" style={{ fontSize: '0.85rem', marginTop: 4 }}>
          Frequent on/off cycling can indicate low refrigerant or a
          failing pressure switch.
        </div>
      )}
    </div>
  );
}

// ---- Tier 3: transient events timeline --------------------------------

function EventTimelinePanel({
  data, clipDurationSec,
}: { data: EventTimelineResult; clipDurationSec: number }) {
  const events = data.events;
  if (!events || events.length === 0) {
    return <div className="muted">No transient events above threshold.</div>;
  }
  const W = 1200, H = 60;
  const dur = clipDurationSec > 0 ? clipDurationSec : 1;
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
           style={{ width: '100%', height: H, display: 'block',
                    background: 'var(--panel-2)', borderRadius: 4 }}>
        <line x1={0} y1={H/2} x2={W} y2={H/2} stroke="var(--border)" strokeWidth="0.5" />
        {events.map((ev, i) => {
          const xs = (ev.time_start_sec / dur) * W;
          const xe = (ev.time_end_sec / dur) * W;
          const color = ev.kind === 'broadband-burst' ? 'var(--danger)'
                      : ev.kind === 'tonal-burst' ? '#f59e0b'
                      : ev.kind === 'click' ? '#fb7185'
                      : 'var(--accent-2)';
          const w = Math.max(1.5, xe - xs);
          return (
            <rect key={i} x={xs} y={6} width={w} height={H - 12}
                  fill={color} fillOpacity={0.5} stroke={color} strokeWidth="1" />
          );
        })}
      </svg>
      <table style={{ fontSize: '0.82rem',
                       fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                       marginTop: 6 }}>
        <thead>
          <tr style={{ color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
            <th style={{ textAlign: 'right' }}>time</th>
            <th style={{ textAlign: 'right' }}>dur</th>
            <th style={{ textAlign: 'right' }}>centroid</th>
            <th style={{ textAlign: 'left', paddingLeft: 8 }}>kind</th>
          </tr>
        </thead>
        <tbody>
          {events.map((ev: DetectedEvent, i: number) => {
            const tone = ev.kind === 'broadband-burst' ? 'var(--danger)'
                       : ev.kind === 'tonal-burst' ? '#f59e0b'
                       : ev.kind === 'click' ? '#fb7185'
                       : 'var(--accent-2)';
            return (
              <tr key={i}>
                <td style={{ textAlign: 'right' }}>{ev.time_start_sec.toFixed(2)}s</td>
                <td style={{ textAlign: 'right' }}>{(ev.duration_sec * 1000).toFixed(0)}ms</td>
                <td style={{ textAlign: 'right' }}>{ev.centroid_hz.toFixed(0)} Hz</td>
                <td style={{ paddingLeft: 8, color: tone }}>{ev.kind}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
